import { Link } from "react-router-dom";
import { Tiles } from "@/components/motion/Tiles";
import { ArrowRight, CalendarDays, FileCheck2, HardHat, ShieldCheck, Timer, Wrench } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/layout/PageHeader";
import { CtaBand } from "@/components/layout/CtaBand";
import { Item, Lines, Reveal, Rule, Stagger } from "@/components/motion/Reveal";

const FORMULE = [
  { title: "Giornaliero", text: "Per interventi rapidi: manutenzioni, potature, piccoli scavi. Consegna la mattina, ritiro a fine giornata.", tag: "da 1 giorno" },
  { title: "Settimanale", text: "La formula più scelta dalle imprese: tariffa ridotta e mezzo a disposizione per tutta la settimana lavorativa.", tag: "5–7 giorni" },
  { title: "Mensile e lungo termine", text: "Per cantieri strutturati. Tariffa dedicata, sostituzione garantita in caso di fermo e manutenzione inclusa.", tag: "30+ giorni" },
];

const PLUS = [
  { icon: ShieldCheck, title: "Mezzi verificati", text: "Ogni mezzo esce con verifica funzionale e documentazione in regola." },
  { icon: HardHat, title: "A freddo o con operatore", text: "Noleggi il solo mezzo oppure con il nostro operatore qualificato." },
  { icon: Timer, title: "Risposta in giornata", text: "Disponibilità e prezzo entro poche ore negli orari di apertura." },
  { icon: Wrench, title: "Assistenza inclusa", text: "In caso di guasto interveniamo o sostituiamo il mezzo." },
  { icon: FileCheck2, title: "Contratto chiaro", text: "Tariffe trasparenti, senza costi nascosti. Preventivo scritto." },
  { icon: CalendarDays, title: "Prenotazione anticipata", text: "Blocca il mezzo per la data che ti serve, anche con settimane di anticipo." },
];

const STEPS = [
  ["01", "Ci racconti il lavoro", "Altezza da raggiungere, spazi, tipo di terreno, durata. Anche solo via WhatsApp."],
  ["02", "Ti proponiamo il mezzo", "Ti indichiamo il mezzo più adatto con disponibilità e preventivo scritto."],
  ["03", "Consegniamo in cantiere", "Portiamo il mezzo nel giorno e nell'orario concordati, pronto a lavorare."],
  ["04", "Ritiriamo a fine noleggio", "Al termine passiamo a ritirarlo. Tu pensi solo al cantiere."],
];

export default function Noleggio() {
  return (
    <>
      <Seo title="Noleggio mezzi da cantiere a Catania" description="Noleggio piattaforme aeree, escavatori e minipale a Catania: giornaliero, settimanale, mensile. A freddo o con operatore. Mezzi verificati e assistenza inclusa." path="/servizi/noleggio" />
      <PageHeader
        eyebrow="Servizi · Noleggio"
        lines={["Noleggio semplice,", "senza sorprese."]}
        description="Dal singolo giorno al lungo termine: formule flessibili, tariffe chiare e un referente unico che conosce ogni mezzo della flotta."
        crumbs={[{ label: "Servizi" }, { label: "Noleggio" }]}
        image="/images/scissor.jpg"
        cta={{ label: "Richiedi un preventivo", to: "/contatti" }}
      />

      <section className="bg-white py-20 sm:py-28" data-testid="noleggio-formule">
        <div className="container-x">
          <Reveal y={10}><p className="eyebrow flex items-center gap-3"><Tiles />Formule</p></Reveal>
          <Lines as="h2" lines={["Scegli la durata,", "al resto pensiamo noi."]} className="display mt-3 text-4xl text-ink sm:text-5xl lg:text-6xl" />
          <Stagger className="mt-14 grid gap-px bg-ink/10 md:grid-cols-3" gap={0.12}>
            {FORMULE.map((f) => (
              <Item key={f.title} className="group bg-white p-8 transition-colors duration-500 hover:bg-paper sm:p-10">
                <span className="font-mono text-[10px] uppercase tracking-widest text-blue-600">{f.tag}</span>
                <h3 className="display mt-6 text-3xl text-ink">{f.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-ink/60 sm:text-base">{f.text}</p>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="relative overflow-hidden bg-ink py-20 text-white sm:py-28 grain" data-testid="noleggio-plus">
        <div className="absolute inset-0 blueprint opacity-50" />
        <div className="container-x relative">
          <Reveal y={10}><p className="eyebrow text-blue-500 flex items-center gap-3"><Tiles />Perché DESP</p></Reveal>
          <Lines as="h2" lines={["Quello che è incluso,", "sempre."]} className="display mt-3 text-4xl sm:text-5xl lg:text-6xl" />
          <Stagger className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-3" gap={0.08}>
            {PLUS.map((p) => (
              <Item key={p.title} className="border-t border-white/15 pt-6">
                <p.icon className="h-6 w-6 text-blue-500" strokeWidth={1.6} />
                <h3 className="display mt-5 text-2xl">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/60">{p.text}</p>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-paper py-20 sm:py-28 blueprint-light" data-testid="noleggio-steps">
        <div className="container-x">
          <Reveal y={10}><p className="eyebrow flex items-center gap-3"><Tiles />Come funziona</p></Reveal>
          <Lines as="h2" lines={["Quattro passaggi,", "zero burocrazia."]} className="display mt-3 text-4xl text-ink sm:text-5xl lg:text-6xl" />
          <div className="mt-14 space-y-0">
            {STEPS.map(([n, t, d], i) => (
              <Reveal key={n} delay={i * 0.08}>
                <div className="grid gap-4 py-7 sm:grid-cols-12 sm:items-baseline">
                  <span className="font-mono text-sm text-blue-600 sm:col-span-1">{n}</span>
                  <h3 className="display text-3xl text-ink sm:col-span-4">{t}</h3>
                  <p className="text-ink/60 sm:col-span-7">{d}</p>
                </div>
                <Rule />
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <Link to="/parco-mezzi" className="btn-dark" data-testid="noleggio-fleet-link">Vedi i mezzi disponibili <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
